import { CSSProperties, FC, useEffect, useMemo, useState } from 'react';
import { useAssistantState } from '@assistant-ui/react';
import clsx from 'clsx';

import editSquare from '@atom/components/common/svgIcons/editSquare.svg';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, List, Menu, Skeleton } from '@atom/mui';
import colors from '@atom/styles/colors';

import { THREADS_PER_PAGE } from './hooks/useThreads';
import { useChatContext } from './ChatContext';
import ChatThreadDeleteModal from './ChatThreadDeleteModal';
import ChatThreadRenameModal from './ChatThreadRenameModal';
import ChatThreadsLoading from './ChatThreadsLoading';

import classes from './chat.module.css';

const styles: Record<string, CSSProperties> = {
  // allows for icon to have hover/ripple background while maintaining alignment
  iconAlignLeft: {
    marginLeft: '-0.5rem',
  },
  iconAlignRight: {
    marginRight: '-0.5rem',
  },
  hamburgerIcon: {
    fontSize: '1.5rem',
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  listItemText: {
    marginTop: 0,
    marginBottom: 0,
    lineHeight: '1.25rem',
    fontWeight: 400,
  },
  newChatIcon: {
    fontSize: '1.25rem',
    marginRight: '1rem',
  },
  menuIcon: {
    height: '1.25rem',
    width: '1.25rem',
  },
  menuItemIcon: {
    marginRight: '1rem',
  },
};

const ChatThreads: FC = () => {
  const [{ isNewThread }, { updateSearchParams }] = useNavigation({
    threadId: SearchParamType.STRING,
    isNewThread: SearchParamType.BOOLEAN,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [refreshedThreadIds, setRefreshedThreadIds] = useState<string[]>([]);
  const thread = useAssistantState(state => state.thread);

  const {
    threadsData: { updateThread, deleteThread, startNewThread },
    ui: { drawerCollapsed, toggleDrawerCollapsed },
  } = useChatContext();

  const {
    threadId,
    threadsData: {
      threads,
      currentThread,
      loading,
      refreshingThread,
      hasMoreThreads,
      fetchMoreThreads,
      refreshThread,
    },
    error,
  } = useChatContext();

  const isCurrentThreadTitleRefreshed = useMemo(() => {
    return refreshedThreadIds.includes(currentThread?.id);
  }, [currentThread, refreshedThreadIds]);

  const handleNewChat = () => {
    if (thread.messages.length) {
      startNewThread();
    }
  };

  const handleMouseEnter = (id: string) => {
    setHovered(id);
  };

  const handleMouseLeave = (id: string) => {
    setHovered(current => (current === id ? null : current));
  };

  const handleDeleteClick = (id: string) => {
    setSelected(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setSelected(null);
    setDeleteModalOpen(false);
  };

  const handleRenameClick = (id: string) => {
    setSelected(id);
    setRenameModalOpen(true);
  };

  const handleRenameClose = () => {
    setSelected(null);
    setRenameModalOpen(false);
  };

  const hasAssistantAnswered = useMemo(
    () =>
      thread.messages.some(
        message => message.role === 'assistant' && !!message.content.length,
      ),
    [thread.messages],
  );

  const isFirstMessage = useMemo(
    () =>
      thread.messages.filter(message => message.role === 'user').length === 1,
    [thread.messages],
  );

  useEffect(() => {
    if (thread.isRunning || isCurrentThreadTitleRefreshed || error) {
      return;
    }

    const getIsUntitled = () => {
      const normalized = currentThread?.title?.toLowerCase();

      return (
        normalized?.startsWith('new thread') ||
        normalized?.startsWith('untitled')
      );
    };

    const isUntitled = getIsUntitled();

    if (!isUntitled) {
      return;
    }

    if (hasAssistantAnswered) {
      refreshThread(threadId);
      setRefreshedThreadIds(current => [...current, currentThread.id]);
    }
  }, [
    thread.isRunning,
    error,
    threadId,
    hasAssistantAnswered,
    currentThread,
    refreshThread,
    isCurrentThreadTitleRefreshed,
  ]);

  if (drawerCollapsed) {
    return (
      <>
        <div className={clsx(classes.threadsHeader, classes.collapsed)}>
          <IconButton
            size="small"
            style={styles.hamburgerIcon}
            onClick={toggleDrawerCollapsed}
            tooltip="Show chats"
          >
            <Icon color={colors.neutral.gray}>menu</Icon>
          </IconButton>
        </div>
        <div className={classes.threadsCenter}>
          <IconButton
            size="small"
            style={{
              ...styles.newChatIcon,
              marginRight: 0,
            }}
            onClick={handleNewChat}
            tooltip="New chat"
          >
            <Icon color={colors.neutral.gray}>
              <img src={editSquare} />
            </Icon>
          </IconButton>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={classes.threadsHeader}>
        <IconButton
          size="small"
          style={{ ...styles.hamburgerIcon, ...styles.iconAlignLeft }}
          onClick={toggleDrawerCollapsed}
          tooltip="Hide chats"
        >
          <Icon color={colors.neutral.gray}>menu</Icon>
        </IconButton>
      </div>
      <List style={styles.list}>
        <List.ListItemButton onClick={handleNewChat} selected={isNewThread}>
          <Icon style={styles.newChatIcon} color={colors.neutral.gray}>
            <img src={editSquare} />
          </Icon>
          <List.ListItemText
            style={{ ...styles.listItemText, fontWeight: 500 }}
            primaryTextStyle={{ ...styles.listItemText, fontWeight: 500 }}
          >
            New Chat
          </List.ListItemText>
        </List.ListItemButton>
        <div className={classes.threadsRecentChats}>Recent Chats</div>
        {threads.map(thr => {
          const { title } = thr;
          const isHovered = thr.id === hovered;

          const isUntitled =
            !thr.title || thr.title?.toLowerCase().startsWith('new thread');

          const isFirstAssistantMessageLoading =
            isFirstMessage && thread.isRunning;

          const isTitlePending =
            threadId === thr.id &&
            (isFirstAssistantMessageLoading || refreshingThread);

          return (
            <List.ListItemButton
              key={thr.id}
              onMouseEnter={() => handleMouseEnter(thr.id)}
              onMouseLeave={() => handleMouseLeave(thr.id)}
              selected={threadId === thr.id && !isTitlePending}
              onClick={() => {
                updateSearchParams({
                  threadId: thr.id,
                  isNewThread: undefined,
                });
              }}
            >
              {isTitlePending ? (
                <Skeleton style={{ width: '100%', height: '1.5rem' }} />
              ) : (
                <>
                  <List.ListItemText
                    primary={isUntitled ? 'Untitled' : title}
                    primaryTextStyle={styles.listItemText}
                    style={styles.listItemText}
                  />
                  <Menu
                    IconButtonProps={{
                      style: {
                        ...styles.menuIcon,
                        ...styles.iconAlignRight,
                        visibility: isHovered ? 'visible' : 'hidden',
                      },
                    }}
                    IconProps={{
                      color: colors.neutral.gray,
                    }}
                  >
                    <Menu.MenuItem onClick={() => handleRenameClick(thr.id)}>
                      <Icon style={styles.menuItemIcon}>edit</Icon>
                      Rename
                    </Menu.MenuItem>
                    <Menu.MenuItem onClick={() => handleDeleteClick(thr.id)}>
                      <Icon style={styles.menuItemIcon}>delete</Icon>
                      Delete
                    </Menu.MenuItem>
                  </Menu>
                </>
              )}
            </List.ListItemButton>
          );
        })}
        <ChatThreadsLoading loading={loading} numThreads={THREADS_PER_PAGE} />
        {hasMoreThreads && (
          <List.ListItemButton
            style={{ color: colors.neutral.gray }}
            onClick={fetchMoreThreads}
          >
            Load More
          </List.ListItemButton>
        )}
      </List>
      <ChatThreadDeleteModal
        open={deleteModalOpen}
        isActive={selected === threadId}
        threadId={selected}
        onClose={handleDeleteClose}
        onDelete={deleteThread}
      />
      <ChatThreadRenameModal
        open={renameModalOpen}
        threadId={selected}
        threads={threads}
        onClose={handleRenameClose}
        onRename={updateThread}
      />
    </>
  );
};

export default ChatThreads;
